<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <edit-employee-layout :employee-id="empNumber">
    <div class="orangehrm-horizontal-padding orangehrm-vertical-padding">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('pim.change_profile_picture') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <div class="orangehrm-employee-picture">
            <profile-image-input
              v-model="employee.empPicture"
              :rules="rules.empPicture"
              :img-src="profilePicUrl"
            />
          </div>
        </oxd-form-row>
        <oxd-form-row>
          <div class="orangehrm-employee-picture">
            <profile-id-image-input
              v-model="employee.empIDCard"
              :rules="rules.empIDCard"
              :img-src="profileIDPicUrl"
            />
          </div>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </edit-employee-layout>
</template>

<script>
import {computed, ref, watch} from 'vue';
import {APIService} from '@ohrm/core/util/services/api.service';
import ProfileImageInput from '@/orangehrmPimPlugin/components/ProfileImageInput';
import ProfileIDImageInput from '@/orangehrmPimPlugin/components/ProfileIDImageInput';
import EditEmployeeLayout from '@/orangehrmPimPlugin/components/EditEmployeeLayout';
import {
  maxFileSize,
  required,
  validFileTypes,
} from '@/core/util/validation/rules';
const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;
const defaultIDPic = `${window.appGlobal.publicPath}/images/default-idcard.png`;

const employeeModel = {
  empPicture: null,
  empIDCard: null,
};

export default {
  components: {
    'profile-image-input': ProfileImageInput,
    'profile-id-image-input': ProfileIDImageInput,
    'edit-employee-layout': EditEmployeeLayout,
  },

  props: {
    empNumber: {
      type: String,
      required: true,
    },
    allowedImageTypes: {
      type: Array,
      required: true,
    },
  },

  setup(props) {
    const employee = ref({
      ...employeeModel,
    });
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.empNumber}/picture`,
    );
    const imgSrc = computed(() => {
      return props.empNumber
        ? `${window.appGlobal.baseUrl}/pim/viewPhoto/empNumber/${props.empNumber}`
        : defaultPic;
    });
    const idcardSrc = computed(() => {
      return props.empNumber
        ? `${window.appGlobal.baseUrl}/pim/viewIDCard/empNumber/${props.empNumber}`
        : defaultIDPic;
    });

    const maxSize = 1024;
    const quality = 0.9;
    let isResizing = false;

    const base64Size = (base64String) => {
      const padding = (base64String.match(/=+$/) || []).length;
      return (base64String.length * 3) / 4 - padding;
    };

    watch(
      () => employee.value.empIDCard,
      (newValue, oldValue) => {
        if (isResizing || !newValue.base64) {
          return;
        }
        isResizing = true;

        const validImageTypes = ['image/jpeg', 'image/png'];
        if (!validImageTypes.includes(newValue.type)) {
          this.$toast.error('請選擇有效的圖像文件（JPEG 或 PNG）');
          return;
        }

        console.log(
          'Employee empIDCard changed from',
          oldValue,
          'to',
          newValue,
        );

        const img = new Image();
        img.src = `data:${newValue.type};base64,${newValue.base64}`;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let width = img.width;
          let height = img.height;

          // 调整图片的尺寸
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // 在canvas上绘制图片
          ctx.drawImage(img, 0, 0, width, height);

          // 将canvas内容转换为Base64格式的图片
          const resizedImage = canvas
            .toDataURL('image/jpeg', quality)
            .split(',')[1];

          employee.value.empIDCard.base64 = resizedImage;
          employee.value.empIDCard.size = base64Size(resizedImage);

          isResizing = false;
        };
      },
    );

    watch(
      () => employee.value.empPicture,
      (newValue, oldValue) => {
        if (isResizing || !newValue.base64) {
          return;
        }
        isResizing = true;

        const validImageTypes = ['image/jpeg', 'image/png'];
        if (!validImageTypes.includes(newValue.type)) {
          this.$toast.error('請選擇有效的圖像文件（JPEG 或 PNG）');
          return;
        }

        console.log(
          'Employee empPicture changed from',
          oldValue,
          'to',
          newValue,
        );

        const img = new Image();
        img.src = `data:${newValue.type};base64,${newValue.base64}`;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let width = img.width;
          let height = img.height;

          // 调整图片的尺寸
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // 在canvas上绘制图片
          ctx.drawImage(img, 0, 0, width, height);

          // 将canvas内容转换为Base64格式的图片
          const resizedImage = canvas
            .toDataURL('image/jpeg', quality)
            .split(',')[1];

          employee.value.empPicture.base64 = resizedImage;
          employee.value.empPicture.size = base64Size(resizedImage);

          isResizing = false;
        };
      },
    );

    return {
      http,
      imgSrc,
      idcardSrc,
      employee,
    };
  },

  data() {
    return {
      isLoading: false,
      empPicture: null,
      empIDCard: null,
      rules: {
        empPicture: [
          maxFileSize(1024 * 1024 * 20),
          validFileTypes(this.allowedImageTypes),
        ],
        empIDCard: [
          maxFileSize(1024 * 1024 * 20),
          validFileTypes(this.allowedImageTypes),
        ],
      },
    };
  },

  computed: {
    profilePicUrl() {
      if (this.employee.empPicture) {
        const file = this.employee.empPicture.base64;
        const type = this.employee.empPicture.type;
        const isPicture = this.allowedImageTypes.findIndex(
          (item) => item === type,
        );
        return isPicture > -1 ? `data:${type};base64,${file}` : defaultPic;
      } else {
        return this.imgSrc;
      }
    },
    profileIDPicUrl() {
      if (this.employee.empIDCard) {
        const file = this.employee.empIDCard.base64;
        const type = this.employee.empIDCard.type;
        const isPicture = this.allowedImageTypes.findIndex(
          (item) => item === type,
        );
        return isPicture > -1 ? `data:${type};base64,${file}` : defaultIDPic;
      } else {
        return this.idcardSrc;
      }
    },
  },

  methods: {
    onSave() {
      console.log('this.employee', this.employee);
      this.isLoading = true;
      this.http
        .request({
          method: 'PUT',
          data: {
            empPicture: this.employee.empPicture,
            empIDCard: this.employee.empIDCard,
          },
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          location.reload();
        });
    },
  },
};
</script>

<style src="./employee.scss" lang="scss" scoped></style>
